/**
 * Analytics SSE event source hook for real-time refresh notifications.
 *
 * Uses fetch-based SSE to support Authorization header (native EventSource
 * doesn't support custom headers).
 */
import type { Accessor } from "solid-js";
import { createEffect, createSignal, onCleanup } from "solid-js";
import { API_BASE_URL } from "./config";
import { readSessionToken } from "./session";

export type ConnectionState = "connecting" | "open" | "closed";

export type AnalyticsEventSource = {
  isRefreshing: Accessor<boolean>;
  connectionState: Accessor<ConnectionState>;
};

/**
 * Parse SSE events from a text chunk.
 * SSE format: "event: <type>\ndata: <json>\n\n"
 */
function parseSSEEvents(chunk: string): Array<{ type: string; data: string }> {
  const events: Array<{ type: string; data: string }> = [];
  const lines = chunk.split("\n");

  let currentEvent: { type: string; data: string } | null = null;

  for (const line of lines) {
    if (line.startsWith("event: ")) {
      currentEvent = { type: line.slice(7).trim(), data: "" };
    } else if (line.startsWith("data: ") && currentEvent) {
      currentEvent.data = line.slice(6);
      events.push(currentEvent);
      currentEvent = null;
    }
  }

  return events;
}

/**
 * Creates a reactive SSE connection to the analytics events endpoint.
 *
 * @param orgId - Accessor for the organisation ID. When null, no connection is made.
 * @returns Object containing reactive signals for refresh status and connection state.
 */
export function createAnalyticsEventSource(
  orgId: Accessor<string | null>
): AnalyticsEventSource {
  const [isRefreshing, setIsRefreshing] = createSignal(false);
  const [connectionState, setConnectionState] = createSignal<ConnectionState>("closed");

  createEffect(() => {
    const id = orgId();
    if (!id) {
      setConnectionState("closed");
      return;
    }

    const controller = new AbortController();
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

    const connect = async () => {
      const token = readSessionToken();
      if (!token) {
        setConnectionState("closed");
        return;
      }

      setConnectionState("connecting");

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/analytics/organisations/${id}/events`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "text/event-stream"
            },
            signal: controller.signal
          }
        );

        if (!response.ok) {
          setConnectionState("closed");
          return;
        }

        if (!response.body) {
          setConnectionState("closed");
          return;
        }

        setConnectionState("open");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const events = parseSSEEvents(chunk);

          for (const event of events) {
            if (event.type === "refresh-started") {
              setIsRefreshing(true);
            } else if (event.type === "refresh-completed") {
              setIsRefreshing(false);
            }
            // Ignore heartbeat events
          }
        }

        // Stream ended, try to reconnect after delay
        setConnectionState("closed");
        if (!controller.signal.aborted) {
          reconnectTimeout = setTimeout(connect, 5000);
        }
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          // Expected when cleanup runs
          return;
        }
        setConnectionState("closed");
        // Reconnect after delay on error
        if (!controller.signal.aborted) {
          reconnectTimeout = setTimeout(connect, 5000);
        }
      }
    };

    void connect();

    onCleanup(() => {
      controller.abort();
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      setConnectionState("closed");
    });
  });

  return { isRefreshing, connectionState };
}
